import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Loader2, Building2, User, Check, Copy, Eye, EyeOff, RefreshCw, Users, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const INDUSTRIES = [
  "SaaS / Software",
  "E-commerce / Retail",
  "Healthcare / Medical",
  "Financial Services",
  "Education / EdTech",
  "Manufacturing",
  "Media / Entertainment",
  "Professional Services",
  "Technology / IT",
  "Other"
];

const TEAM_SIZES = [
  "1 (Just me)",
  "2-5",
  "6-10",
  "11-25",
  "26-50",
  "51-100",
  "100+"
];

const formatPrice = (cents) => {
  if (!cents) return 'Free';
  return `$${(cents / 100).toFixed(0)}/mo`;
};

const CreateOrganizationModal = ({ isOpen, onClose, onSuccess }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Step management: 1=Org, 2=Plan, 3=User, 4=Success
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Step 1: Organization data
  const [orgName, setOrgName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  
  // Step 2: Plan data
  const [grids, setGrids] = useState([]);
  const [gridsLoading, setGridsLoading] = useState(false);
  const [selectedGrid, setSelectedGrid] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [skipStripe, setSkipStripe] = useState(true);
  
  // Step 3: User data
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [authMethod, setAuthMethod] = useState('password');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requirePasswordChange, setRequirePasswordChange] = useState(true);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);

  // Fetch grids when entering step 2
  useEffect(() => {
    if (step === 2 && grids.length === 0) {
      loadGrids();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const loadGrids = async () => {
    setGridsLoading(true);
    try {
      const res = await api.get('/api/admin/org-plans/grids');
      const allGrids = res.data.grids || [];
      setGrids(allGrids);
      // Auto-select the live grid if there is one
      const liveGrid = allGrids.find(g => g.is_live);
      if (liveGrid && !selectedGrid) {
        setSelectedGrid(liveGrid.version_id);
        if (liveGrid.plans?.length > 0) {
          setSelectedPlan(liveGrid.plans[0].plan_id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch grids:', err);
      toast.error('Failed to load pricing grids');
    } finally {
      setGridsLoading(false);
    }
  };
  
  // Generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let newPassword = '';
    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPassword);
    setShowPassword(true);
  };
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };
  
  const copyAllCredentials = () => {
    if (result?.credentials) {
      const creds = result.credentials;
      let text = `Login URL: ${creds.login_url}\nEmail: ${creds.email}`;
      if (creds.password) {
        text += `\nPassword: ${creds.password}`;
      } else if (creds.auth_method === 'google') {
        text += `\nAuth: Sign in with Google`;
      }
      navigator.clipboard.writeText(text);
      toast.success('All credentials copied to clipboard');
    }
  };
  
  const resetForm = () => {
    setStep(1);
    setResult(null);
    setOrgName('');
    setCompanyWebsite('');
    setIndustry('');
    setTeamSize('');
    setSelectedGrid(null);
    setSelectedPlan(null);
    setSkipStripe(true);
    setGrids([]);
    setUserName('');
    setUserEmail('');
    setAuthMethod('password');
    setPassword('');
    setShowPassword(false);
    setRequirePasswordChange(true);
    setSendWelcomeEmail(true);
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  // Validation
  const isStep1Valid = () => orgName.trim().length >= 2;
  
  const isStep2Valid = () => !!selectedGrid && !!selectedPlan;
  
  const isStep3Valid = () => {
    if (!userName.trim() || userName.trim().length < 2) return false;
    if (!userEmail.trim() || !userEmail.includes('@')) return false;
    if (authMethod === 'password' && password && password.length < 8) return false;
    return true;
  };

  // Get selected plan details
  const getSelectedPlanDetails = () => {
    const grid = grids.find(g => g.version_id === selectedGrid);
    return grid?.plans?.find(p => p.plan_id === selectedPlan);
  };

  const getSelectedPlanType = () => {
    const plan = getSelectedPlanDetails();
    return plan?.plan_type || 'free';
  };
  
  // Submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    const planType = getSelectedPlanType();
    const isPaid = planType === 'paid' || planType === 'inquire';
    
    try {
      const response = await api.post('/api/admin/metrics/organizations/create', {
        organization: {
          name: orgName.trim(),
          company_website: companyWebsite.trim() || null,
          industry: industry || null,
          team_size: teamSize || null
        },
        plan: {
          tier: isPaid ? 'paid' : 'free',
          plan_id: selectedPlan,
          grid_version_id: selectedGrid,
          skip_stripe: isPaid ? skipStripe : true,
          start_with_trial: false,
          trial_days: 14
        },
        first_user: {
          name: userName.trim(),
          email: userEmail.trim().toLowerCase(),
          auth_method: authMethod,
          password: authMethod === 'password' ? (password || null) : null,
          require_password_change: requirePasswordChange
        },
        notifications: {
          send_welcome_email: sendWelcomeEmail,
          notify_admin_on_first_login: false
        }
      });
      
      setResult(response.data);
      setStep(4);
      toast.success('Organization created successfully');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPlanDetails = getSelectedPlanDetails();
  const isPaidPlan = getSelectedPlanType() === 'paid' || getSelectedPlanType() === 'inquire';
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={cn("max-w-2xl max-h-[85vh] overflow-y-auto", colors.bgCard)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 4 ? (
              <>
                <Check className="w-5 h-5 text-green-500" />
                Organization Created
              </>
            ) : (
              <>
                <Building2 className="w-5 h-5" />
                Create Organization
                <span className={cn("text-sm font-normal ml-2", colors.textMuted)}>
                  Step {step} of 3
                </span>
              </>
            )}
          </DialogTitle>
          {step !== 4 && (
            <DialogDescription>
              {step === 1 && 'Enter organization details'}
              {step === 2 && 'Select a pricing grid and plan'}
              {step === 3 && 'Set up the first user (owner)'}
            </DialogDescription>
          )}
        </DialogHeader>
        
        {/* Step 1: Organization Details */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name *</Label>
                <Input
                  id="orgName"
                  data-testid="create-org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website">Company Website</Label>
                <Input
                  id="website"
                  data-testid="create-org-website"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://acme.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger data-testid="create-org-industry">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Team Size</Label>
                  <Select value={teamSize} onValueChange={setTeamSize}>
                    <SelectTrigger data-testid="create-org-team-size">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAM_SIZES.map(size => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button data-testid="create-org-next-plan" onClick={() => setStep(2)} disabled={!isStep1Valid()}>
                Next: Select Plan
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="space-y-4">
            {gridsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {grids.map(grid => (
                  <Card 
                    key={grid.version_id}
                    data-testid={`grid-card-${grid.version_id}`}
                    className={cn(
                      "p-4 border-2 cursor-pointer transition-all",
                      selectedGrid === grid.version_id ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : colors.borderPrimary,
                      colors.bgCard
                    )}
                    onClick={() => {
                      setSelectedGrid(grid.version_id);
                      if (grid.plans?.length > 0 && !grid.plans.find(p => p.plan_id === selectedPlan)) {
                        setSelectedPlan(grid.plans[0].plan_id);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={cn("font-semibold", colors.textPrimary)}>{grid.name}</h4>
                          {grid.is_live && (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Live</Badge>
                          )}
                        </div>
                        <p className={cn("text-xs mt-1", colors.textMuted)}>
                          Variant {grid.variant} - Deployed {new Date(grid.deployed_at).toLocaleDateString()}
                          {grid.org_count > 0 && ` - ${grid.org_count} org${grid.org_count > 1 ? 's' : ''}`}
                        </p>
                      </div>
                      {selectedGrid === grid.version_id && (
                        <Check className="w-5 h-5 text-blue-500" />
                      )}
                    </div>

                    {selectedGrid === grid.version_id && (
                      <div className="mt-4 pt-4 border-t space-y-2">
                        <p className={cn("text-sm font-medium", colors.textMuted)}>Select Plan:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {grid.plans?.map(plan => (
                            <div
                              key={plan.plan_id}
                              data-testid={`plan-card-${plan.plan_id}`}
                              className={cn(
                                "p-3 rounded-lg border-2 cursor-pointer transition-all",
                                selectedPlan === plan.plan_id 
                                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" 
                                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(plan.plan_id);
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span className={cn("font-medium", colors.textPrimary)}>{plan.label}</span>
                                <span className={cn("text-sm font-bold", colors.textPrimary)}>
                                  {formatPrice(plan.price_cents)}
                                </span>
                              </div>
                              <p className={cn("text-xs mt-1", colors.textMuted)}>
                                {plan.plan_type === 'free' ? 'No credit card' : plan.plan_type === 'inquire' ? 'Custom pricing' : 'Per paid seat'}
                              </p>
                              {plan.restrictions && (
                                <div className={cn("text-xs mt-2 space-y-0.5", colors.textMuted)}>
                                  {plan.restrictions.max_paid_seats && (
                                    <p className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      Max {plan.restrictions.max_paid_seats} paid seats
                                    </p>
                                  )}
                                  {plan.restrictions.max_products && (
                                    <p className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      Max {plan.restrictions.max_products} products
                                    </p>
                                  )}
                                  {plan.restrictions.max_features && (
                                    <p className="flex items-center gap-1">
                                      <Sparkles className="w-3 h-3" />
                                      Max {plan.restrictions.max_features} features
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {/* Manual billing option for paid plans */}
            {isPaidPlan && (
              <div className={cn("border rounded-lg p-3", colors.border)}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="skipStripe"
                    data-testid="create-org-skip-stripe"
                    checked={skipStripe}
                    onCheckedChange={setSkipStripe}
                  />
                  <Label htmlFor="skipStripe" className={cn("text-sm cursor-pointer", colors.textMuted)}>
                    Skip Stripe setup (manual billing)
                  </Label>
                </div>
              </div>
            )}
            
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button data-testid="create-org-next-user" onClick={() => setStep(3)} disabled={!isStep2Valid()}>
                Next: First User
              </Button>
            </DialogFooter>
          </div>
        )}
        
        {/* Step 3: First User */}
        {step === 3 && (
          <div className="space-y-4">
            {/* Plan summary */}
            {selectedPlanDetails && (
              <Card className={cn("p-3 border", colors.bgSecondary, colors.borderPrimary)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-500" />
                    <span className={cn("text-sm font-medium", colors.textPrimary)}>{orgName}</span>
                  </div>
                  <Badge variant="outline">
                    {selectedPlanDetails.label} - {formatPrice(selectedPlanDetails.price_cents)}
                  </Badge>
                </div>
              </Card>
            )}

            <div className="space-y-4">
              <h3 className={cn("text-sm font-medium flex items-center gap-2", colors.textPrimary)}>
                <User className="w-4 h-4" />
                First User (Owner)
              </h3>
              
              <div className="space-y-2">
                <Label htmlFor="userName">Full Name *</Label>
                <Input
                  id="userName"
                  data-testid="create-org-user-name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Smith"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="userEmail">Email Address *</Label>
                <Input
                  id="userEmail"
                  data-testid="create-org-user-email"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@acme.com"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <h3 className={cn("text-sm font-medium", colors.textPrimary)}>Authentication Method</h3>
              
              <div className={cn("border rounded-lg p-3 space-y-3", colors.border)}>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="authGoogle"
                    name="auth"
                    checked={authMethod === 'google'}
                    onChange={() => setAuthMethod('google')}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="authGoogle" className="cursor-pointer">
                    <span className="font-medium">Google Sign-In</span>
                    <p className={cn("text-sm", colors.textMuted)}>User will sign in with Google on first login</p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="authPassword"
                    name="auth"
                    checked={authMethod === 'password'}
                    onChange={() => setAuthMethod('password')}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="authPassword" className="cursor-pointer">
                    <span className="font-medium">Password</span>
                    <p className={cn("text-sm", colors.textMuted)}>Set a password for email login</p>
                  </Label>
                </div>
                
                {authMethod === 'password' && (
                  <div className="ml-7 pt-2 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="password"
                            data-testid="create-org-password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Leave blank to auto-generate"
                            className="pr-10 font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className={cn("absolute right-2 top-1/2 -translate-y-1/2", colors.textMuted)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button variant="outline" size="icon" onClick={generatePassword} title="Generate password">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className={cn("text-xs", colors.textMuted)}>
                        {password ? (password.length < 8 ? 'Must be at least 8 characters' : 'Password set') : 'A secure password will be generated if left blank'}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="requireChange"
                        checked={requirePasswordChange}
                        onCheckedChange={setRequirePasswordChange}
                      />
                      <Label htmlFor="requireChange" className={cn("text-sm cursor-pointer", colors.textMuted)}>
                        Require password change on first login
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <h3 className={cn("text-sm font-medium", colors.textPrimary)}>Notifications</h3>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="welcomeEmail"
                  checked={sendWelcomeEmail}
                  onCheckedChange={setSendWelcomeEmail}
                />
                <Label htmlFor="welcomeEmail" className={cn("text-sm cursor-pointer", colors.textMuted)}>
                  Send welcome email to user (includes login credentials)
                </Label>
              </div>
            </div>
            
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button data-testid="create-org-submit" onClick={handleSubmit} disabled={!isStep3Valid() || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Organization'
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
        
        {/* Step 4: Success */}
        {step === 4 && result && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 space-y-2">
              <p className="text-green-700 dark:text-green-400 font-medium">Organization: {result.organization?.name}</p>
              <p className="text-green-700 dark:text-green-400">Plan: {selectedPlanDetails?.label || result.organization?.plan_tier}</p>
              <p className="text-green-700 dark:text-green-400">User: {result.user?.email} (owner)</p>
              {result.notifications_sent?.includes('welcome_email') && (
                <p className="text-green-700 dark:text-green-400">Welcome email sent</p>
              )}
            </div>
            
            <div className={cn("border rounded-lg p-4", colors.border)}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={cn("font-medium", colors.textPrimary)}>Login Credentials</h4>
                <Button variant="outline" size="sm" onClick={copyAllCredentials}>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy All
                </Button>
              </div>
              
              <div className="space-y-2 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <span className={colors.textMuted}>URL:</span>
                  <div className="flex items-center gap-2">
                    <span>{result.credentials?.login_url}</span>
                    <button onClick={() => copyToClipboard(result.credentials?.login_url, 'URL')}>
                      <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={colors.textMuted}>Email:</span>
                  <div className="flex items-center gap-2">
                    <span>{result.credentials?.email}</span>
                    <button onClick={() => copyToClipboard(result.credentials?.email, 'Email')}>
                      <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                </div>
                
                {result.credentials?.password ? (
                  <div className="flex items-center justify-between">
                    <span className={colors.textMuted}>Password:</span>
                    <div className="flex items-center gap-2">
                      <span>{result.credentials.password}</span>
                      <button onClick={() => copyToClipboard(result.credentials.password, 'Password')}>
                        <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className={colors.textMuted}>Auth:</span>
                    <span>Sign in with Google</span>
                  </div>
                )}
              </div>
            </div>
            
            {result.errors?.length > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-yellow-700 dark:text-yellow-400 font-medium text-sm">Warnings:</p>
                {result.errors.map((err, idx) => (
                  <p key={idx} className="text-yellow-600 dark:text-yellow-400 text-sm">{err}</p>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button data-testid="create-org-close" onClick={handleClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrganizationModal;
